/**
 * `useVerifiedAction` — per-action email-verification gate.
 *
 * The front-door `<EmailVerificationGate>` only blocks brand-new unverified
 * signups. This guards the boundary actions (password reset, invites, billing)
 * for every unverified account. A call site hands its whole click handler to
 * `run`; it runs at once, or is held behind an inline prompt on the current
 * screen that offers a resend, polls Auth0's live `/userinfo`, and runs the
 * handler automatically once the address verifies.
 *
 * Shares the single `email-verification-gate` flag, claim signal, verified
 * latch, and resend path with the front door. No per-action flag.
 */

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { toIdFromHex } from '#~/api/utils.js';
import { ApiError } from '#~/api/types.js';
import { getVerificationEmailSentFor, resendVerificationEmail } from '#~/auth/utils.js';
import {
  EMAIL_VERIFICATION_ACTION_PRECHECK_TIMEOUT_MS,
  EMAIL_VERIFICATION_POLL_INTERVAL_MS,
  EMAIL_VERIFICATION_RESEND_COOLDOWN_MS
} from './constants.js';
import { decideActionGate, remainingCooldownMs, type GatedAction } from './logic.js';
import { useVerificationSignal } from './use-verification-signal.js';
import { VerifyToContinue } from './verify-to-continue.jsx';

/** A call site's complete handler, including its own error handling. */
type Proceed = () => unknown;

/** A held action plus whether its pre-prompt live check has settled. */
interface HeldAction {
  proceed: Proceed;
  checked: boolean;
}

export interface VerifiedAction {
  /**
   * Run `proceed` now, or hold it behind the inline verify prompt. Resolves
   * when an allowed `proceed` settles, so callers can keep their loading state;
   * resolves at once when the action is held.
   */
  run: (proceed: Proceed) => Promise<void>;
  /** The inline prompt. Render it anywhere in the call site; `null` when idle. */
  prompt: ReactElement | null;
}

/** Hook form. `action` picks the prompt copy only; the decision is shared. */
export const useVerifiedAction = (action: GatedAction): VerifiedAction => {
  // defaultValue:false ⇒ a loading or errored flag resolves to OFF (transparent).
  const { enabled } = useFeatureFlag(PostHogFeatureFlag.EMAIL_VERIFICATION_GATE, {
    defaultValue: false
  });
  const { claims, signal, refresh } = useVerificationSignal();
  const { getAccessTokenSilently } = useAuth0();
  const studioUser = useDepot((µ) => µ.user);

  const outcome = decideActionGate({
    flagEnabled: enabled,
    signal,
    registryEmailVerified: studioUser?.emailVerified
  });

  const [held, setHeld] = useState<HeldAction | undefined>(undefined);
  // Mirrors `held` synchronously so a second click in the same tick sees the first.
  const heldRef = useRef<HeldAction | undefined>(undefined);
  const [sentAt, setSentAt] = useState<number | undefined>(undefined);
  const [cooldownAt, setCooldownAt] = useState<number | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [sendError, setSendError] = useState<string | undefined>(undefined);
  const [now, setNow] = useState(() => Date.now());

  // `run` is handed to click handlers that may have been created on an earlier
  // render, so it reads the decision through a ref instead of a stale closure.
  const outcomeRef = useRef(outcome);
  outcomeRef.current = outcome;

  const run = useCallback(
    async (proceed: Proceed): Promise<void> => {
      if (outcomeRef.current === 'allow') {
        await proceed();
        return;
      }

      // One held action at a time: a second click before the prompt appears is
      // dropped, never swapped in for the first.
      if (heldRef.current !== undefined) {
        return;
      }

      const next: HeldAction = { proceed, checked: false };
      heldRef.current = next;
      setHeld(next);
      const showPrompt = (): void => {
        setHeld((current) => (current === next ? { ...next, checked: true } : current));
      };
      // Re-read the live claim before showing anything. A user who verified in
      // another tab still carries a stale `false` claim on this page; this check
      // lets that user straight through instead of flashing a verify prompt.
      // Bounded so a hung /userinfo still shows the prompt and its Cancel.
      const timer = setTimeout(showPrompt, EMAIL_VERIFICATION_ACTION_PRECHECK_TIMEOUT_MS);
      void refresh()
        .catch(() => undefined)
        .finally(() => {
          clearTimeout(timer);
          showPrompt();
        });
    },
    [refresh]
  );

  // Auto-proceed: once the decision flips to allow (verified, or the flag was
  // turned off), run the held handler exactly once and clear it.
  useEffect(() => {
    if (held === undefined || outcome !== 'allow') {
      return;
    }
    heldRef.current = undefined;
    setHeld(undefined);
    void held.proceed();
  }, [held, outcome]);

  // Poll the live claim while an action is held. A failed poll is ignored: the
  // user stays in place, can cancel, and the next tick tries again.
  useEffect(() => {
    if (held === undefined || outcome === 'allow') {
      return;
    }
    const interval = setInterval(() => {
      void refresh().catch(() => undefined);
    }, EMAIL_VERIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [held, outcome, refresh]);

  // Tick the clock while a resend cooldown is active so the countdown renders.
  useEffect(() => {
    if (cooldownAt === undefined) {
      return;
    }
    const interval = setInterval(() => {
      setNow(Date.now());
      if (remainingCooldownMs(cooldownAt, Date.now(), EMAIL_VERIFICATION_RESEND_COOLDOWN_MS) <= 0) {
        clearInterval(interval);
      }
    }, 1_000);
    return () => clearInterval(interval);
  }, [cooldownAt]);

  const handleResend = useCallback((): void => {
    // Client cooldown is a UX hint; the server throttle is authoritative.
    if (
      sendingRef.current ||
      remainingCooldownMs(cooldownAt, Date.now(), EMAIL_VERIFICATION_RESEND_COOLDOWN_MS) > 0
    ) {
      return;
    }
    // Keep pending distinct from sent, and block same-tick duplicate clicks.
    sendingRef.current = true;
    setSending(true);
    setSendError(undefined);

    void (async () => {
      try {
        const token = await getAccessTokenSilently();
        const idBytes = toIdFromHex(studioUser?.id ?? '');
        if (!idBytes || !claims.email) {
          throw new Error('Verification email destination unavailable');
        }
        await resendVerificationEmail({ id: idBytes, token, email: claims.email });
        const at = Date.now();
        setSentAt(at);
        setCooldownAt(at);
        setNow(at);
      } catch (error) {
        if (error instanceof ApiError && error.code === 429) {
          // Respect the server throttle without treating its rejection as sent.
          const at = Date.now();
          setCooldownAt(at);
          setNow(at);
          setSendError('Please wait a minute before trying to send another verification email.');
        } else {
          setSendError("Couldn't send the verification email. Please try again.");
        }
      } finally {
        // Per settle-every-terminal-branch (ai.knowledge/global): token and
        // request failures must both release Send for a user-initiated retry.
        sendingRef.current = false;
        setSending(false);
      }
    })();
  }, [cooldownAt, getAccessTokenSilently, studioUser, claims.email]);

  const handleCancel = useCallback((): void => {
    heldRef.current = undefined;
    setHeld(undefined);
  }, []);

  // The boot auto-send may already have sent this address a link this session.
  const sent =
    sentAt !== undefined ||
    (claims.email !== undefined && getVerificationEmailSentFor() === claims.email);

  const prompt =
    held !== undefined && held.checked && outcome === 'verify' ? (
      <VerifyToContinue
        action={action}
        email={claims.email}
        sent={sent}
        sending={sending}
        sendError={sendError}
        cooldownRemainingMs={remainingCooldownMs(
          cooldownAt,
          now,
          EMAIL_VERIFICATION_RESEND_COOLDOWN_MS
        )}
        onResend={handleResend}
        onCancel={handleCancel}
      />
    ) : null;

  return { run, prompt };
};
